// src/hooks/useAppUpdate.jsx
import { useState, useEffect } from 'react';
import { useToast } from './use-toast';
import { getVersionInfo } from '../config/version';

export const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(getVersionInfo().formattedVersion);
  const { toast } = useToast();

  useEffect(() => {
    // Check if service worker is supported
    if ('serviceWorker' in navigator) {
      // Listen for service worker updates
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setUpdateAvailable(false);
        setIsUpdating(false);
        toast({
          title: "App Updated",
          description: "The app has been updated to the latest version.",
        });
        // Reload the page to apply updates
        window.location.reload();
      });

      // Listen for waiting service worker (update available)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
          setUpdateAvailableStatus(true);
        }
      });

      // Check for updates on mount
      checkForUpdates();
      
      // Set up periodic update checks (every 5 minutes)
      const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [toast]);

  const checkForUpdates = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          
          // Check if there's a waiting service worker (update available)
          if (registration.waiting) {
            setUpdateAvailableStatus(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  };

  const triggerUpdate = async () => {
    setIsUpdating(true);
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.waiting) {
          // Send message to service worker to skip waiting
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          toast({
            title: "Updating App",
            description: "Applying the latest version...",
          });
        } else {
          // Force update check
          await registration?.update();
          toast({
            title: "Checking for Updates",
            description: "Checking for the latest version...",
          });
          
          // Check again after a short delay
          setTimeout(async () => {
            if (registration.waiting) {
              setUpdateAvailableStatus(true);
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error triggering update:', error);
      toast({
        title: "Update Error",
        description: "Failed to update the app. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const setUpdateAvailableStatus = (available) => {
    setUpdateAvailable(available);
    if (available) {
      toast({
        title: "Update Available",
        description: "A new version of the app is available.",
      });
    }
  };

  return {
    updateAvailable,
    isUpdating,
    currentVersion,
    triggerUpdate,
    checkForUpdates,
    setUpdateAvailableStatus,
  };
}; 